export const SUGGESTED_ACTION_CN: Record<string, string> = {
  run_reconcile_consumption_wallet: '把钱包扣款补齐',
  retry_fetch: '重新抓一次数据',
  clamp_to_zero: '当作 0 处理',
  accept_zero: '接受 0 值',
  manual_review_or_estimate: '人工核一下, 或先按估值入账',
  verify_data_pipeline: '排查是不是抓数环节漏了',
  manual_review: '人工核一下',
  audit_t7_stable: '等 7 天后数据稳定再核对',
  audit_fetch_pipeline: '排查是不是抓数环节漏了',
  bind_account_to_customer: '去广告账户页给它绑客户',
  t7_converged: '7 天后已自动收敛, 无需处置',
}

export const WASABI_TASK_LABELS: Record<string, string> = {
  'wasabi.drain_webhook_events': 'Wasabi Webhook 事件处理 (2m)',
  'wasabi.purge_webhook_events': 'Wasabi Webhook 事件清理 (每日 04:17)',
  'wasabi.resolve_uncertain_writes': 'Wasabi 不确定写操作反查 (5m)',
  'wasabi.sync_accounts_full': 'Wasabi 账户与余额同步 (10m)',
  'wasabi.sync_cards_today': 'Wasabi 当日卡片同步 (30m)',
  'wasabi.sync_transactions_today': 'Wasabi 当天交易同步 (每小时 :42)',
  'wasabi.sync_transactions_yesterday_final': 'Wasabi 昨日最终结算 (每日 07:30)',
  'wasabi.check_yesterday_finalization': 'Wasabi 昨日结算补投检查 (每日 09:25)',
  'wasabi.auto_cancel_banned_account_cards': 'Wasabi 封禁账户立即注销 (已停用)',
  'wasabi.auto_freeze_banned_account_cards': 'Wasabi 封禁账户冻卡 (30m)',
}

export const WASABI_SCHEDULED_AUDIT_ACTION_LABELS: Record<string, string> = {
  'wasabi.card.auto_cancel_summary': 'Wasabi 卡片定时注销',
  'wasabi.auto_cancel_ban': 'Wasabi 封禁账户卡片注销',
  'wasabi.auto_freeze_ban': 'Wasabi 封禁账户冻卡',
  'wasabi.webhook.dispatch_summary': 'Wasabi Webhook 事件处理',
  'wasabi.webhook.purge': 'Wasabi Webhook 事件清理',
  'wasabi.write.resolve_summary': 'Wasabi 不确定写操作反查',
}

export const WASABI_SCHEDULED_RESOURCE_LABELS: Record<string, string> = {
  wasabi_card: 'Wasabi 卡片',
  wasabi_webhook_event: 'Wasabi Webhook 事件',
  wasabi_write_intent: 'Wasabi 写操作',
}

export function buildBatchImportAuditSummary(extra: Record<string, any>): string {
  const created = extra.created ?? extra.count ?? '?'
  return `导入广告账户${extra.filename ? ` (${extra.filename})` : ''} — 新建 ${created}`
    + `${extra.skipped ? ` · 跳过 ${extra.skipped}` : ''}`
    + `${extra.errors_count ? ` · 错误 ${extra.errors_count}` : ''}`
    + `${extra.wasabi_card_bound ? ` · Wasabi 绑卡 ${extra.wasabi_card_bound}` : ''}`
    + `${extra.wasabi_card_customer_assigned ? ` · 自动归属 ${extra.wasabi_card_customer_assigned}` : ''}`
    + `${extra.wasabi_card_warnings_count ? ` · 警告 ${extra.wasabi_card_warnings_count}` : ''}`
}

export interface AnomalyTypeInfo {
  label: string
  color: string
}

export const ANOMALY_TYPE_CN: Record<string, AnomalyTypeInfo> = {
  zero_value: { label: '整天 0 元 (疑似漏抓)', color: 'gold' },
  spike: { label: '比平时高很多', color: 'orange' },
  drop: { label: '比平时低很多', color: 'red' },
  currency_mismatch: { label: '币种不符', color: 'red' },
  pagination_failed: { label: '抓取分页失败', color: 'red' },
  status_change: { label: '账户状态变更', color: 'blue' },
  stagnant_value: { label: '数字卡住没动', color: 'gold' },
  negative_spend: { label: '负数消耗', color: 'red' },
  t7_major_drop: { label: '7 天后大幅缩水', color: 'volcano' },
  t7_unexpected_rise: { label: '7 天后反而上涨', color: 'purple' },
  unbound_account: { label: '账户没绑客户', color: 'magenta' },
  wallet_consumption_drift: { label: '钱包账目对不上', color: 'red' },
  xlsx_higher_than_api: { label: '人工导入高于 API (自检)', color: 'cyan' },
}

export const STAGING_STATUS_CN: Record<string, AnomalyTypeInfo> = {
  pending: { label: '待处置', color: 'orange' },
  resolved_self_healed: { label: '已自动消解', color: 'green' },
  resolved_auto: { label: '已系统判定', color: 'green' },
  resolved_manual: { label: '已人工处置', color: 'green' },
  resolved_timeout: { label: '已超时关闭', color: 'default' },
}

export const SUMMARY_STATUS_CN: Record<string, AnomalyTypeInfo> = {
  pending: { label: '待对账', color: 'default' },
  aligned: { label: '已对齐', color: 'green' },
  variance_alert: { label: '差额告警·已分摊', color: 'gold' },
  variance_review: { label: '待审批', color: 'orange' },
  resolved: { label: '已处置', color: 'green' },
}

export interface DataLineageInfo {
  icon: string
  label: string
  color: string
  tagColor: string
  desc: string
}

export const DATA_LINEAGE_CN: Record<string, DataLineageInfo> = {
  api_stable: {
    icon: '●', label: 'API 稳定', color: 'var(--apex-color-success, #52c41a)', tagColor: 'green',
    desc: 'Meta API 拉到的 T+7 终值, 不会再变',
  },
  api_provisional: {
    icon: '◐', label: 'API 临时', color: 'var(--apex-color-warning, #fa8c16)', tagColor: 'orange',
    desc: 'T+0 当天临时数, 7 天后会被官方终值覆盖',
  },
  manual: {
    icon: '✎', label: '手工录入', color: 'var(--apex-color-info, #1677ff)', tagColor: 'blue',
    desc: '财务/运营在系统手工录入',
  },
  conservative_estimate: {
    icon: '≈', label: '保守估值', color: 'var(--apex-color-warning, #d48806)', tagColor: 'gold',
    desc: '抓不到数时按客户日均上限保守估算',
  },
  post_audit_corrected: {
    icon: '✓', label: '事后修正', color: 'var(--apex-color-secondary, #722ed1)', tagColor: 'purple',
    desc: '事后审计调整过的金额',
  },
  customer_summary_allocated: {
    icon: '∑', label: '汇总分摊 (历史)', color: 'var(--apex-color-info, #13c2c2)', tagColor: 'cyan',
    desc: '历史「客户汇总录入」按比例分摊到具体账户的金额, 该流程已下线 (2026-05-01)',
  },
  legacy_unknown: {
    icon: '?', label: '历史未知', color: 'var(--apex-color-textSecondary, #8c8c8c)', tagColor: 'default',
    desc: '迁数据前的存量记录, 来源不明',
  },
}

export const CONSUMPTION_SOURCE_CN: Record<
  string,
  Pick<DataLineageInfo, 'label' | 'tagColor' | 'desc'>
> = {
  card_spend_reconcile: {
    label: '光子易',
    tagColor: 'gold',
    desc: '该金额采用光子易卡扣费参与多来源对账后的结果',
  },
  wasabi_card_spend_reconcile: {
    label: 'Wasabi 扣费',
    tagColor: 'green',
    desc: '该金额采用 Wasabi 卡扣费参与多来源对账后的结果',
  },
}

export interface WalletStatusInfo {
  color: string
  label: string
  threshold: string
}

// 2026-05-04: 客户资金 3 选 1 互斥状态 (替代旧 alert_level/wallet_status 两套档位)
export interface FundStatusInfo {
  color: string
  label: string
  desc: string
}

export const FUND_STATUS_CN: Record<string, FundStatusInfo> = {
  overdue: {
    color: 'red', label: '欠款',
    desc: '余额已为负,客户欠款',
  },
  low_balance: {
    color: 'orange', label: '余额不足',
    desc: '按当前节奏不足 7 天可用',
  },
  healthy: {
    color: 'green', label: '正常',
    desc: '余额充足',
  },
}

export const WALLET_STATUS_CN: Record<string, WalletStatusInfo> = {
  healthy: {
    color: 'green', label: '健康',
    threshold: '余额 ≥ 7 天日均消耗',
  },
  watch: {
    color: 'gold', label: '观察',
    threshold: '余额支撑 3-7 天日均消耗',
  },
  tight: {
    color: 'orange', label: '紧张·启保守估值',
    threshold: '余额支撑 1-3 天日均消耗, 系统自动启用保守估值',
  },
  critical: {
    color: 'red', label: '告急',
    threshold: '余额不足 1 天日均消耗 或 已欠款, 投放自动暂停',
  },
}

export const POLICY_CHANGE_TYPE_CN: Record<string, { label: string; color: string }> = {
  initial: { label: '初始政策', color: 'default' },
  add_media_policy: { label: '新增媒体', color: 'cyan' },
  update_media_policy: { label: '媒体调整', color: 'blue' },
  cooperation_mode_change: { label: '合作模式变更', color: 'purple' },
  fee_mode_change: { label: '费率模式变更', color: 'magenta' },
  credit_term_change: { label: '账期变更', color: 'gold' },
  recharge_credit_change: { label: '充值授信变更', color: 'gold' },
  policy_correction: { label: '历史订正', color: 'orange' },
  approval_correction: { label: '修正审批通过', color: 'orange' },
}

export const COOPERATION_MODE_CN: Record<string, string> = {
  prepaid: '预付',
  prepaid_consumption: '预付实消',
  credit: '账期',
}

export const FEE_MODE_CN: Record<string, string> = {
  service_fee: '服务费',
  rebate: '返点',
}

export const POLICY_FIELD_CN: Record<string, string> = {
  service_fee_rate: '服务费率',
  handling_fee_rate: '手续费(USD)',
  handling_fee_rate_usdt: '手续费(U)',
  rebate_rate: '返点率',
  cooperation_mode: '合作模式',
  fee_mode: '费率模式',
  credit_term_days: '账期天数',
  media_type: '媒体',
  recharge_credit_limit: '充值授信额度',
  recharge_credit_source: '授信来源',
  recharge_credit_manual_mode: '填写模式',
  recharge_credit_protected_cycle: '保护周期',
}

export const MEDIA_TYPE_CN: Record<string, string> = {
  meta_overseas: 'Meta海外',
  meta_domestic: 'Meta国内',
  google_overseas: 'Google海外',
  google_domestic: 'Google国内',
  tiktok_overseas: 'TikTok海外',
  tiktok_domestic: 'TikTok国内',
}

// Meta API disable_reason 整数码 → 中文标签
// 与 backend/app/services/account_status_categorizer.py 保持一致, 改一处改两处
export const DISABLE_REASON_LABEL_CN: Record<number, string> = {
  1: '违规封禁',
  2: 'IP 审查',
  3: '支付风控',
  4: '灰账',
  5: 'AFC 审查',
  6: '业务诚信审查',
  7: '永久关闭',
  8: '未使用永久关闭',
  9: '未结算',
}

// 「支付失败」判定集; 与后端 PAYMENT_FAILURE_REASONS 同步
export const PAYMENT_FAILURE_REASONS: ReadonlySet<number> = new Set([3, 9])

export function disableReasonLabel(reason: number | null | undefined): string {
  if (reason === null || reason === undefined) return ''
  return DISABLE_REASON_LABEL_CN[reason] || `未知(${reason})`
}

const BACKEND_ERROR_CN: Array<[RegExp, string]> = [
  [/Unauthorized|401/i, '登录已过期, 请重新登录'],
  [/Forbidden|403/i, '当前角色没有权限做这个操作'],
  [/Not\s*Found|404/i, '记录不存在或已被删除'],
  [/version.*mismatch|optimistic.*lock|stale.*version/i, '这条记录刚被其他人改过, 请刷新页面重试'],
  [/duplicate.*key|UNIQUE.*constraint|already.*exists/i, '已存在相同记录, 不要重复提交'],
  [/foreign.*key|violates.*foreign/i, '关联的记录不存在, 检查上游数据'],
  [/insufficient.*balance|balance.*negative|negative.*balance/i, '余额不足, 请先充值'],
  [/refund.*exceed|exceed.*consumption/i, '退款金额超过该客户累计消耗, 不允许'],
  [/spend_cap.*invalid|invalid.*spend_cap/i, '账户上限设置不合法'],
  [/approval.*not.*found|approval.*expired/i, '审批单已过期或不存在, 请重新发起'],
  [/approval.*already.*processed|already.*approved|already.*rejected/i, '该审批已被处理, 不要重复操作'],
  [/payload.*signature|hmac.*verify|signature.*invalid/i, '审批单内容已被改动, 验签失败 - 请联系创始人'],
  [/google.*sheet|drive.*api/i, '协同表服务异常, 请稍后再试'],
  [/telegram|chat_id|bot.*token/i, 'Telegram 推送失败, 检查群是否绑定'],
  [/timeout|timed.*out/i, '操作超时, 请重试; 如多次失败联系技术'],
  [/connection.*refused|ECONNREFUSED|database.*connection/i, '后台服务暂时不可达, 请稍候重试'],
  [/file.*too.*large|payload.*too.*large/i, '文件过大, 请压缩或拆分后重试'],
  [/invalid.*file|unsupported.*format/i, '文件格式不支持, 请检查模板'],
  [/csrf|origin/i, '安全校验失败, 请刷新页面重新登录'],
  [/rate.*limit|too.*many.*requests|429/i, '操作太频繁, 请稍候再试'],
]

export function translateBackendError(detail: string | undefined | null): string {
  if (!detail) return '操作失败'
  const s = String(detail)
  for (const [re, cn] of BACKEND_ERROR_CN) {
    if (re.test(s)) return cn
  }
  if (/^[A-Za-z0-9_\s.,:'"-]+$/.test(s) && s.length > 80) {
    return '操作失败 - 请联系技术 (堆栈见控制台)'
  }
  return s
}

export function suggestedActionLabel(value: string | null | undefined): string {
  if (!value) return '-'
  return SUGGESTED_ACTION_CN[value] || value
}

interface AuditImportFileLog {
  id: number
  action: string
  has_import_file: boolean
  import_file_mode: string | null
  detail: Record<string, any> | null
}

export function getAuditImportFileDownload(log: AuditImportFileLog): {
  url: string
  filename: string
  label: string
} | null {
  if (!log.has_import_file || ![
    'batch_import.ad_accounts',
    'batch_import.consumptions',
  ].includes(log.action)) return null

  const rawFilename = String(log.detail?.extra?.filename || `import_${log.id}.xlsx`)
  const filename = rawFilename.split(/[\\/]/).pop() || `import_${log.id}.xlsx`
  if (log.import_file_mode !== 'redacted') {
    return {
      url: `/api/audit-logs/${log.id}/import-file`,
      filename,
      label: '下载原文件',
    }
  }

  const dot = filename.lastIndexOf('.')
  const redactedFilename = dot > 0
    ? `${filename.slice(0, dot)}_脱敏${filename.slice(dot)}`
    : `${filename}_脱敏.xlsx`
  return {
    url: `/api/audit-logs/${log.id}/import-file`,
    filename: redactedFilename,
    label: '下载脱敏文件',
  }
}
